import { FC, useEffect, useState } from 'react';
import { AnimatePresence } from 'motion/react';
import { MdKeyboardArrowDown, MdKeyboardArrowUp } from 'react-icons/md';
import { DropdownProps, Styled } from './';
import { useComponentVisible } from '../../../utils/hooks';
import { useI18n } from 'contexts/i18n';

const Dropdown: FC<DropdownProps> = ({
  label,
  options,
  onChange,
  selectedValue = '',
  isError = false,
  isDisabled = false,
  width,
  size = 'sm',
  background,
  isLoading = false,
  testIdContainer = 'dropdown-container',
  testIdBody = 'dropdown-body',
  noBorder = false,
}) => {
  const {
    ref: dropdownRef,
    isComponentVisible: isOpen,
    setIsComponentVisible: setOpen,
  } = useComponentVisible(false);
  const [selectedItem, setSelectedItem] = useState('');
  const i18n = useI18n();
  const dropDownAnimation = {
    enter: {
      opacity: 1,
      y: 5,
    },
    initial: {
      y: -20,
      opacity: 0,
    },
  };

  const toggleDropdown = () => {
    if (isDisabled) {
      return false;
    }
    setOpen(!isOpen);
  };

  const handleDropdownItemClick = (value: string) => {
    setSelectedItem(value);
    onChange(value);
    setOpen(false);
  };

  useEffect(() => {
    setSelectedItem(selectedValue);
  }, [selectedValue]);

  return (
    <Styled.DropdownContainer
      tabIndex={0}
      isError={isError}
      isDisabled={isDisabled}
      noBorder={noBorder}
      width={width}
      ref={dropdownRef}
      background={background}
      data-testid={testIdContainer}
    >
      <Styled.DropdownHeader
        onClick={toggleDropdown}
        className="header"
        size={size}
        isLoading={isLoading}
      >
        {!isLoading && (
          <>
            <Styled.DropdownLabel isPlaceholder={!selectedItem}>
              {selectedItem
                ? options.find(item => item.value == selectedItem)?.label
                : label}
            </Styled.DropdownLabel>
            <div>
              {isOpen ? (
                <MdKeyboardArrowUp size="24px" />
              ) : (
                <MdKeyboardArrowDown size="24px" />
              )}
            </div>
          </>
        )}
        {isLoading && <Styled.DropdownLoadingSpinner />}
      </Styled.DropdownHeader>

      <AnimatePresence>
        {isOpen && (
          <Styled.DropdownBody
            data-testid={testIdBody}
            variants={dropDownAnimation}
            initial="initial"
            animate={'enter'}
            transition={{ type: 'tween' }}
            exit={{
              y: 0,
              opacity: 0,
            }}
            width={dropdownRef.current?.clientWidth}
          >
            {options.map(item => (
              <Styled.Option
                onClick={() => handleDropdownItemClick(item.value)}
                key={item.value}
              >
                {i18n[item.label] || item.label}
              </Styled.Option>
            ))}
          </Styled.DropdownBody>
        )}
      </AnimatePresence>
    </Styled.DropdownContainer>
  );
};

export default Dropdown;
